import { useApi } from "@/hooks";
import Icon from "@Components/generic/icon";
import { Actions, Events } from "@Context/common/general/context";
import VRHeaderButtonType from "@Enums/VRHeaderButtonType";
import { faBackspace } from "@fortawesome/pro-solid-svg-icons";
import useGlobalContext from "@hooks/common/general/use-global-context";
import useI18n from "@i18n";
import { Api } from "@Shared/schemas";
import { isNumber } from "lodash-es";
import { createContext, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useNotificationDialog } from "../general/notification-dialog-context";

interface IPinButton {
  label: string | JSX.Element;
  value: number;
}

interface IPinEntryContext {
  pin: string;
  error: boolean;
  attempts: number;
  lockEndTime: number;
  user: any; // Replace with proper user type
  PIN_BUTTONS: IPinButton[];
  PIN_LENGTH: number;
  handlePinInput: (value: string) => void;
  handleBackspace: () => void;
  handleLogout: () => void;
}

// Constants
export const PIN_LENGTH = 4;
export const MAX_ATTEMPTS = 3;
export const ERROR_RESET_DELAY = 1000;

const PIN_BUTTONS: IPinButton[] = [
  ...[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => ({
    label: String(num),
    value: num,
  })),
  {
    label: "", //Empty button for correct spacing
    value: 10,
  },
  {
    label: "0",
    value: 0,
  },
  {
    label: <Icon icon={faBackspace} />,
    value: -1,
  },
];

const PinEntryContext = createContext<IPinEntryContext | undefined>(undefined);

interface PinEntryProviderProps {
  children: React.ReactNode;
  userId: string;
  onSuccessNavigateTo?: string;
  isMobile?: boolean;
}

export const PinEntryProvider = ({ children, userId, onSuccessNavigateTo = "/home", isMobile = false }: PinEntryProviderProps) => {
  const { globalState, dispatch } = useGlobalContext();
  const { showNotification } = useNotificationDialog();
  const { i18n } = useI18n();
  const navigate = useNavigate();
  const isWeb = !globalState.device.isUnityEmbedded;
  const [pin, setPin] = useState("");
  const [error, setError] = useState(false);
  const [attempts, setAttempts] = useState(MAX_ATTEMPTS);
  const [lockEndTime, setLockEndTime] = useState(0);

  const { call: getUser, response: user } = useApi<typeof Api.Users.GetUser>({
    api: Api.Users.GetUser,
    request: {
      user_doc_id: userId,
    },
  });

  const handleReAuthSuccess = () => {
    Events.auth.switchProfileSuccess.emit();
    if (isWeb) {
      navigate(onSuccessNavigateTo);
    } else {
      Events.app.navigate.emit({ path: onSuccessNavigateTo, reset: true });
    }
  };

  const onSwitchProfileSuccess = (data: any) => {
    dispatch({
      type: Actions.FirebaseActions.ReAuthWithToken,
      payload: {
        custom_token: data.custom_token,
      },
      onSuccess: handleReAuthSuccess,
    });
  };

  const handlePinError = (remainingAttempts: number) => {
    setPin("");
    setError(true);
    setAttempts(remainingAttempts);

    setTimeout(() => {
      setError(false);
    }, ERROR_RESET_DELAY);
  };

  const handleUnknownError = () => {
    showNotification({
      title: i18n("error"),
      message: i18n("account.pin.entry.error.unknown"),
      type: "error",
    });
  };

  const handlePinSubmit = (pinToSubmit: string) => {
    dispatch({
      type: Actions.AuthActions.SwitchProfile,
      payload: {
        user_id: userId,
        pin: parseInt(pinToSubmit),
      },
      onSuccess: (data: any) => {
        data.status !== "failure" ? onSwitchProfileSuccess(data) : handlePinError(data.remaining_attempts);
      },
      onError: (data: any) => {
        if (data.details?.lock_end_time && isNumber(data.details.lock_end_time)) {
          setAttempts(0);
          setLockEndTime(data.details.lock_end_time);
        } else {
          handleUnknownError();
        }
      },
    });
  };

  const handlePinInput = (value: string) => {
    if (error) setError(false);

    if (pin.length < PIN_LENGTH) {
      const newPin = pin + value;
      setPin(newPin);

      if (newPin.length === PIN_LENGTH) {
        handlePinSubmit(newPin);
      }
    }
  };

  const handleBackspace = () => {
    setPin(prev => prev.slice(0, -1));
  };

  const handleLogout = () => {
    dispatch({ type: Actions.FirebaseActions.Logout });
  };

  useEffect(() => {
    getUser();

    if (isMobile) {
      Events.app.bottomNavMobileHidden.emit(true);
      return () => {
        Events.app.bottomNavMobileHidden.emit(false);
      };
    } else {
      Events.app.showHeaderButton.emit({ type: VRHeaderButtonType.BACK, title: "" });
      Events.app.setHeaderAltSections.emit({
        centerSection: <></>,
        rightSection: <></>,
      });
      return () => {
        Events.app.setHeaderAltSections.emit(null);
        Events.app.hideHeaderButton.emit();
      };
    }
  }, []);

  useEffect(() => {
    if (user && !isMobile) {
      Events.app.showHeaderButton.emit({
        type: VRHeaderButtonType.BACK,
        title: `${user.first_name.charAt(0).toUpperCase() + user.first_name.slice(1)} ${
          user.last_name.charAt(0).toUpperCase() + user.last_name.slice(1)
        }`,
      });
    }
  }, [user]);

  const value = {
    pin,
    error,
    attempts,
    lockEndTime,
    user,
    handlePinInput,
    handleBackspace,
    handleLogout,
    PIN_LENGTH,
    PIN_BUTTONS,
  };

  return <PinEntryContext.Provider value={value}>{children}</PinEntryContext.Provider>;
};

export const usePinEntry = () => {
  const context = useContext(PinEntryContext);
  if (context === undefined) {
    throw new Error("usePinEntry must be used within a PinEntryProvider");
  }
  return context;
};
